/**
 * Pure helpers for dashboard capture (S14): group tiles into print rows and
 * build the parameter/filter state line rendered in capture headers.
 */
import type { DashboardTile, TileLayout } from '../types/dashboardTypes';

export type CaptureRowTile = { id: string; title: string; layout: TileLayout };

/**
 * Groups tiles into visual rows for the PDF print document: rows in `y`
 * order, tiles left-to-right, `widthFraction` = the tile's share of its
 * row's total grid width (page breaks fall BETWEEN these rows).
 */
export function groupTilesIntoRows(
  tiles: DashboardTile[],
): Array<Array<{ id: string; title: string; widthFraction: number }>> {
  const byRow = new Map<number, CaptureRowTile[]>();
  for (const tile of tiles) {
    const list = byRow.get(tile.layout.y) ?? [];
    list.push({ id: tile.id, title: tile.title, layout: tile.layout });
    byRow.set(tile.layout.y, list);
  }
  return [...byRow.entries()]
    .sort(([a], [b]) => a - b)
    .map(([, row]) => {
      const sorted = row.sort((a, b) => a.layout.x - b.layout.x);
      const totalWidth = sorted.reduce((sum, t) => sum + t.layout.w, 0) || 1;
      return sorted.map((t) => ({
        id: t.id,
        title: t.title,
        widthFraction: t.layout.w / totalWidth,
      }));
    });
}

/**
 * `Parameters: a = 1 · b = x — Filters: channel = web` (undefined when
 * neither is active — the header then shows only title + timestamp).
 */
export function buildCaptureStateLine(
  params: Array<{ label: string; display: string }>,
  filters: Array<{ field: string; display: string }>,
): string | undefined {
  const parts: string[] = [];
  if (params.length > 0) {
    parts.push(`Parameters: ${params.map((p) => `${p.label} = ${p.display}`).join(' · ')}`);
  }
  if (filters.length > 0) {
    parts.push(`Filters: ${filters.map((f) => `${f.field} = ${f.display}`).join(' · ')}`);
  }
  return parts.length > 0 ? parts.join(' — ') : undefined;
}
